import React from 'react';
import { SimulationConfig, BaseBackgroundEffectType } from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { 
  Palette, 
  Settings2, 
  Image as ImageIcon, 
  Video, 
  Sparkles, 
  Layers, 
  Zap,
  Monitor,
  Sun,
  Moon,
  Cloud,
  Wand2,
  Waves
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ControlGroup } from './ControlGroup';
import { InfoPopup } from './InfoPopup';
import { CollapsibleSection } from './CollapsibleSection';

interface BackgroundSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  getLockInfo?: (id: string) => { isLocked: boolean; min_plan?: string };
  onUpgradeClick?: () => void;
}

export const BackgroundSettingsSection: React.FC<BackgroundSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", getLockInfo, onUpgradeClick 
}) => {
  const backgrounds = Object.values(BaseBackgroundEffectType) as BaseBackgroundEffectType[];
  const isLockedFn = (id: string) => getLockInfo?.(id).isLocked || false;

  const categories = [
    { 
      name: 'Geometric', 
      icon: Layers,
      items: [BaseBackgroundEffectType.SOLID, BaseBackgroundEffectType.LINEAR_GRADIENT, BaseBackgroundEffectType.RADIAL_GRADIENT, BaseBackgroundEffectType.VIGNETTE, BaseBackgroundEffectType.CYBER_GRID] 
    },
    { 
      name: 'Dynamic', 
      icon: Waves,
      items: [BaseBackgroundEffectType.MESH_GRADIENT, BaseBackgroundEffectType.PLASMA, BaseBackgroundEffectType.AURORA_BASE, BaseBackgroundEffectType.RGB_WAVE, BaseBackgroundEffectType.KALEIDOSCOPE] 
    },
    { 
      name: 'Atmospheric', 
      icon: Sparkles,
      items: [BaseBackgroundEffectType.STARFIELD, BaseBackgroundEffectType.DEEP_SPACE, BaseBackgroundEffectType.SCANLINE, BaseBackgroundEffectType.NOISE_GRAIN, BaseBackgroundEffectType.MATRIX_CODE] 
    }
  ];

  const randomizeBG = () => {
    const randomType = backgrounds[Math.floor(Math.random() * backgrounds.length)];
    setConfig(p => ({
      ...p,
      backgroundEffect: randomType,
      backgroundPrimaryColor: `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`,
      backgroundSecondaryColor: `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`,
      backgroundTertiaryColor: `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`,
      backgroundSpeed: 0.5 + Math.random() * 2,
      backgroundOpacity: 0.8 + Math.random() * 0.2,
      backgroundComplexity: 1 + Math.random() * 2,
      backgroundRainbowMode: Math.random() > 0.8,
      backgroundAnimate: true,
      backgroundEnabled: true
    }));
  };

  return (
    <CollapsibleSection 
        title="Environment Canvas" 
        icon={Monitor} 
        darkMode={config.darkMode} 
        settingKey="environmentCanvas"
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
    >
        <div className="flex flex-col gap-10 mt-6 text-left">
            {/* 1. Environment Preset */}
            <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Sun className="w-3.5 h-3.5 opacity-20" />
                        <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Atmosphere</h4>
                    </div>
                    <button 
                        onClick={randomizeBG}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all active:scale-95 ${config.darkMode ? 'bg-white/5 border-white/5 text-white/60 hover:text-white' : 'bg-white border-zinc-200 text-zinc-500 shadow-sm hover:bg-zinc-50'}`}
                    >
                        <Wand2 className="w-3 h-3" />
                        <span className="text-[8px] font-black uppercase tracking-widest">Surprise</span>
                    </button>
                </div>

                <div className={`grid grid-cols-3 gap-3 p-1.5 rounded-[2rem] border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-zinc-100 shadow-sm'}`}>
                    <ModeButton 
                        active={config.backgroundMode === 'LIGHT'} 
                        label="Pristine" 
                        icon={Sun} 
                        onClick={() => setConfig(p => ({...p, backgroundMode: 'LIGHT', darkMode: false, backgroundPrimaryColor: '#ffffff', backgroundEnabled: false}))}
                        darkMode={config.darkMode}
                    />
                    <ModeButton 
                        active={config.backgroundMode === 'DARK'} 
                        label="Obsidian" 
                        icon={Moon} 
                        onClick={() => setConfig(p => ({...p, backgroundMode: 'DARK', darkMode: true, backgroundPrimaryColor: '#09090b', backgroundEnabled: false}))}
                        darkMode={config.darkMode}
                    />
                    <ModeButton 
                        active={config.backgroundMode === 'CUSTOM'} 
                        label="Custom" 
                        icon={Cloud} 
                        onClick={() => setConfig(p => ({...p, backgroundMode: 'CUSTOM', backgroundEnabled: true}))}
                        darkMode={config.darkMode}
                    />
                </div>
            </div>

            {/* 2. Effect Matrix */}
            <AnimatePresence>
                {(config.backgroundEnabled || config.backgroundMode === 'CUSTOM') && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="space-y-10"
                    >
                        {categories.map((cat, i) => (
                            <div key={cat.name} className="space-y-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <cat.icon className="w-3.5 h-3.5 opacity-20" />
                                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">{cat.name}</h4>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {cat.items.map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setConfig(p => ({...p, backgroundEffect: type, backgroundEnabled: true}))}
                                            className={`px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest text-left transition-all border ${
                                                config.backgroundEffect === type && config.backgroundEnabled
                                                ? 'bg-neon-cyan text-white shadow-lg border-cyan-500 scale-[1.02]' 
                                                : (config.darkMode ? 'bg-white/5 border-white/5 text-white/40 hover:bg-white/10' : 'bg-white border-zinc-100 text-zinc-400 hover:text-zinc-900 shadow-sm')
                                            }`}
                                        >
                                            {type.replace(/_/g, ' ')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* 3. Parameter Controls */}
                        <div className="space-y-6 pt-6 border-t border-zinc-500/10">
                            <div className="flex items-center gap-2 mb-4">
                                <Palette className="w-3.5 h-3.5 opacity-20" />
                                <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Chroma Engine</h4>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <ColorInput label="Primary" settingKey="backgroundPrimaryColor" value={config.backgroundPrimaryColor} onChange={(v: string) => setConfig(p => ({...p, backgroundPrimaryColor: v}))} />
                                <ColorInput label="Secondary" settingKey="backgroundSecondaryColor" value={config.backgroundSecondaryColor} onChange={(v: string) => setConfig(p => ({...p, backgroundSecondaryColor: v}))} />
                                <ColorInput label="Accent" settingKey="backgroundTertiaryColor" value={config.backgroundTertiaryColor} onChange={(v: string) => setConfig(p => ({...p, backgroundTertiaryColor: v}))} />
                            </div>

                            <div className="space-y-4">
                                <SmartInput label="Alpha" settingKey="backgroundOpacity" min={0} max={1} step={0.01} value={config.backgroundOpacity} onChange={(v: number) => setConfig(p => ({...p, backgroundOpacity: v}))} darkMode={config.darkMode} />
                                <SmartInput label="Flow Speed" settingKey="backgroundSpeed" min={0} max={5} step={0.1} value={config.backgroundSpeed} onChange={(v: number) => setConfig(p => ({...p, backgroundSpeed: v}))} darkMode={config.darkMode} />
                                <SmartInput label="Density" settingKey="backgroundComplexity" min={0.1} max={5} step={0.1} value={config.backgroundComplexity} onChange={(v: number) => setConfig(p => ({...p, backgroundComplexity: v}))} darkMode={config.darkMode} />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    </CollapsibleSection>
  );
};

const ModeButton = ({ active, label, icon: Icon, onClick, darkMode }: any) => (
    <button 
        onClick={onClick}
        className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all ${active ? 'bg-neon-cyan text-white shadow-xl scale-[1.05]' : (darkMode ? 'hover:bg-white/5 text-zinc-500' : 'hover:bg-zinc-50 text-zinc-400')}`}
    >
        <Icon className={`w-5 h-5 ${active ? 'text-white' : 'opacity-40'}`} />
        <span className="text-[9px] font-black uppercase tracking-widest">{label}</span>
    </button>
);

const ColorInput = ({ label, value, onChange, settingKey }: any) => (
    <div className="flex flex-col gap-2 relative">
        <div className="flex items-center justify-center gap-1">
            <span className="text-[8px] font-black uppercase opacity-30 tracking-tighter">{label}</span>
            {settingKey && <InfoPopup content={settingKey} settingKey={settingKey} darkMode={false} />}
        </div>
        <div className="relative h-10 w-full rounded-xl overflow-hidden shadow-sm border border-black/5 ring-4 ring-white/50">
            <input 
                type="color" 
                value={value} 
                onChange={e => onChange(e.target.value)} 
                className="absolute inset-0 w-full h-full cursor-pointer opacity-0 z-10" 
            />
            <div className="absolute inset-0" style={{ backgroundColor: value }} />
        </div>
    </div>
);
